import { useCallback, useEffect, useRef, useState } from "react";

import { WALLET_UNIT, getShowQuote, toShown, toUnit } from "@/lib/wallet/show";
import { useSelector } from "@/store";

const DIGITS = 2;

/** The show-currency text for a USD stake string; text that is not a number passes through. */
const shownText = (usd: string): string => {
  const value = Number(usd);
  if (usd.trim() === "" || !Number.isFinite(value)) return usd;
  return toShown(value).amount.toFixed(DIGITS);
};

/**
 * Money on a game screen. The game keeps every amount in the wallet unit (what it sends, what
 * the server answers, the wallet deltas); only what the player reads and types is converted.
 * Use `fmt` / `money` for any amount the game holds, never for odds, multipliers or chances.
 */
export function useGameMoney() {
  // Read so the screen re-renders when the quote or the unit changes.
  const unit = useSelector((store) => store.auth.balance?.currency) || "";
  const rate = useSelector((store) => store.auth.balance?.show?.rate);
  // Signed out there is no wallet, and the money a visitor reads is USD.
  const code = toShown(0, unit || WALLET_UNIT).currency;

  const fmt = useCallback(
    (amount: number, digits: number = DIGITS) =>
      toShown(Number(amount) || 0, unit || undefined).amount.toFixed(digits),
    // `rate` is not read here, but a new one must give a new function.
    [unit, rate],
  );
  const money = useCallback(
    (amount: number, digits?: number) =>
      code ? `${fmt(amount, digits)} ${code}` : fmt(amount, digits),
    [fmt, code],
  );

  return { code, fmt, money };
}

/**
 * A stake field typed in the show currency for a game that keeps the stake in USD. Hand its
 * `value` / `onChange` to the input; the game's own `value` and setter stay in USD.
 */
export function useShownInput(usd: string, onUsd: (next: string) => void) {
  const rate = useSelector((store) => store.auth.balance?.show?.rate);
  const [text, setText] = useState(() => shownText(usd));
  const sent = useRef(usd);

  // A change from the game (½, 2×, reset) or a new rate rewrites the text; the player's own typing does not.
  useEffect(() => {
    if (usd === sent.current) return;
    sent.current = usd;
    setText(shownText(usd));
  }, [usd]);
  useEffect(() => {
    setText(shownText(sent.current));
  }, [rate]);

  const onChange = useCallback(
    (next: string) => {
      setText(next);
      const value = Number(next);
      // Not a number yet ("", "1."): hand it on as typed, so the game's own check answers.
      const out =
        next.trim() === "" || !Number.isFinite(value) || !getShowQuote()
          ? next
          : toUnit(value).toFixed(DIGITS);
      sent.current = out;
      onUsd(out);
    },
    [onUsd],
  );

  return { value: getShowQuote() ? text : usd, onChange };
}
